import { useState } from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';

import LegalDocumentDialog from 'components/legal/LegalDocumentDialog';
import { formatLegalDate } from 'components/legal/legalUtils';
import { LEGAL_DOCUMENT_LABELS, LEGAL_SLUGS, LegalDocument, LegalDocumentMeta, LegalDocumentType } from 'types/legal';

interface DocumentHistoryProps {
  open: boolean;
  onClose: () => void;
  type: LegalDocumentType;
  // Newest first.
  versions: LegalDocumentMeta[];
  // The version in use right now, when it is one of `versions`.
  currentVersion?: number | null;
  loadVersion: (meta: LegalDocumentMeta) => Promise<LegalDocument>;
}

// Read-only list of published versions; each opens in the document viewer.
const DocumentHistory = ({ open, onClose, type, versions, currentVersion, loadVersion }: DocumentHistoryProps) => {
  const [viewing, setViewing] = useState<LegalDocumentMeta | null>(null);
  const label = LEGAL_DOCUMENT_LABELS[type];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="legal-history-title">
        <DialogTitle id="legal-history-title">{label} history</DialogTitle>
        <DialogContent dividers>
          {versions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No versions published yet.
            </Typography>
          ) : (
            <List disablePadding>
              {versions.map((meta) => (
                <ListItemButton key={meta.uid || meta.version} onClick={() => setViewing(meta)} sx={{ borderRadius: 1 }}>
                  <ListItemText
                    primary={
                      <>
                        Version {meta.version}
                        {currentVersion === meta.version && <Chip label="Current" size="small" color="success" sx={{ ml: 1 }} />}
                      </>
                    }
                    secondary={
                      <span data-no-translate="true">
                        {meta.title}
                        {meta.effectiveDate ? ` · effective ${formatLegalDate(meta.effectiveDate)}` : ''}
                      </span>
                    }
                  />
                  <Typography variant="body2" color="primary">
                    View
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <LegalDocumentDialog
        open={Boolean(viewing)}
        slug={LEGAL_SLUGS[type]}
        load={viewing ? () => loadVersion(viewing) : undefined}
        showOpenInNewTab={false}
        onClose={() => setViewing(null)}
      />
    </>
  );
};

export default DocumentHistory;
